import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { RadioBlockGroup, RadioOptionWrapper, RadioLabel } from 'design-system/src/components/Radio/Radio';
import { ActionCard } from '../../ActionCard/ActionCard';
import { OtherRequestButton } from './shared/OtherRequestButton';
import { client } from '../../../api';
import { tasksQueryKey } from '../../../queries';
import { useSitesOfCare } from '../../../hooks/useBenefitPreferences';
import { formatAddress } from '../../../utils';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import type { Task } from '../types';

type BenefitsRoutingConfirmationActionsProps = {
  /** The task ID */
  taskId: string;
  /** The full task object */
  task: Task;
  /** Callback to show the follow-up task modal */
  setShowNewFollowupTaskModal: (show: boolean) => void;
};

const FULFILLMENT_OPTIONS = [
  {
    value: 'buy_and_bill' as const,
    label: 'Buy-and-bill',
    description: 'Your practice purchases and bills the payer directly',
  },
  {
    value: 'white_bagging' as const,
    label: 'Specialty pharmacy (white bagging)',
    description: 'The specialty pharmacy will ship the medication to the site of care',
  },
];

/** Map a fulfillment selection to the backend response type */
const getResponseType = (selection: string): 'process_as_medical_benefits' | 'process_as_pharmacy_benefits' => {
  if (selection === 'buy_and_bill') return 'process_as_medical_benefits';
  return 'process_as_pharmacy_benefits';
};

const SETTINGS_LINK = '/settings?tab=benefitPreferences';

export const BenefitsRoutingConfirmationActions = ({
  taskId,
  task,
  setShowNewFollowupTaskModal,
}: BenefitsRoutingConfirmationActionsProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: sitesData } = useSitesOfCare();
  const sites = sitesData?.sites ?? [];

  const isCompleted = task.status === 'completed';
  const isDisabled = isCompleted;

  const getInitialSelection = (): string => {
    if (!isCompleted || !task.completed_response) return '';
    if (task.completed_response === 'process_as_medical_benefits') return 'buy_and_bill';
    if (task.completed_response === 'process_as_pharmacy_benefits') return 'white_bagging';
    return '';
  };
  const [selectedOption, setSelectedOption] = useState<string>(getInitialSelection());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const siteOfCareAddress = (() => {
    if (!task.site_of_care_id) return null;
    const site = sites.find((s) => s.id === task.site_of_care_id);
    if (!site) return null;
    return formatAddress(
      [site.address_line_1, site.address_line_2].filter(Boolean).join(', '),
      site.city,
      site.state,
      site.zipcode,
    );
  })();

  const handleSubmit = async () => {
    if (!selectedOption) return;

    const responseType = getResponseType(selectedOption);

    setIsSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('task_response', responseType);
      submitData.append(
        'additional_input',
        JSON.stringify({
          preference_scope: 'prescription',
        }),
      );

      await client.post(`tasks/${taskId}/respond`, submitData);
      toast.custom(() => <Toast variant="success">Task response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    } catch (error) {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isCompleted && task.completed_at && (
        <div className="mb-4 rounded-medium bg-surface-200 p-3">
          <Typography>
            This task was completed on <strong>{formatStandardDate(task.completed_at)}</strong>
            {task.completed_by_name && (
              <>
                {' '}
                by <strong>{task.completed_by_name}</strong>
              </>
            )}
            .{' '}
            <Link to={SETTINGS_LINK} className="underline">
              Manage fulfillment preferences
            </Link>
          </Typography>
        </div>
      )}

      {!isCompleted && (
        <div className="mb-4 flex flex-col gap-1">
          <Typography weight="bold">Please select your fulfillment option for this patient.</Typography>
          <Typography>
            Tandem will ask for a fulfillment option every time you prescribe this drug, according to your settings.
            {siteOfCareAddress && <> Your site of care at {siteOfCareAddress} is already set up.</>}
          </Typography>
        </div>
      )}

      {isCompleted && siteOfCareAddress && (
        <div className="mb-4">
          <Typography>Your site of care at {siteOfCareAddress} is already set up for this drug.</Typography>
        </div>
      )}

      <ActionCard>
        <div className="flex w-full flex-col gap-4">
          <RadioBlockGroup $variant="outlined">
            {FULFILLMENT_OPTIONS.map((option) => (
              <RadioOptionWrapper
                id={`confirmation-${option.value}`}
                name="fulfillment-confirmation"
                value={option.value}
                checked={selectedOption === option.value}
                onChange={() => setSelectedOption(option.value)}
                key={option.value}
                variant="outlined"
                disabled={isDisabled}
              >
                <div className="flex flex-col gap-0.5">
                  <RadioLabel $disabled={isDisabled}>{option.label}</RadioLabel>
                  <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
                    {option.description}
                  </Typography>
                </div>
              </RadioOptionWrapper>
            ))}
          </RadioBlockGroup>
        </div>

        {!isCompleted && (
          <ButtonRow align={Alignment.SpaceBetween} fullWidth>
            <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              disabled={!selectedOption || isSubmitting}
            >
              Submit
            </Button>
          </ButtonRow>
        )}
      </ActionCard>
    </>
  );
};
